import { request } from '../../api/http';
import { asArray, unwrap } from '../../api/normalise';
import type { Id, TicketListItem } from '../../api/types';

/**
 * SPACES — meeting topics and research items, and the promotion bridge.
 *
 * ✅ RECONCILED against `tbs-planhub-api` at `93fd61b` — paths, query names, request bodies and
 * response envelopes are read off `src/routes/spaces.ts` and `src/services/spaces.ts`, not guessed.
 * This file previously described endpoints that did not exist; four things it had inferred were
 * wrong, and the wrong ones are called out below so nobody re-derives them:
 *
 *   · the item path is `/space-items/{id}`, NOT `/spaces/items/{id}`
 *   · request bodies are camelCase — `dueOn`, not `due_on`
 *   · a space carries THREE counts, not one
 *   · `type` and `priority` on promote are OPTIONAL; the API defaults them
 *
 * ⭐ db/001's rule still holds and is what the UI is built around: these are deliberately NOT tickets
 * with a label. No branch, no MR, no sprint. One bridge only — promotion.
 */

/** ⚠️ `space.kind` is a CHECK, not a vocabulary — the README lists it among the FIXED sets. */
export type SpaceKind = 'meeting' | 'research';

export interface Space {
  id: Id;
  kind: SpaceKind | string;
  name: string;
  /**
   * ⭐⭐ THREE COUNTS, NEVER SUMMED. The API computes them separately and its own comment says why:
   * *"A space with 12 items, 4 open and 3 promoted is not '19 of anything', and a single number would
   * be."* An item can be open AND unpromoted, or done AND promoted; one total answers no question
   * anybody has.
   */
  item_count: number;
  open_item_count: number;
  promoted_item_count: number;
}

/**
 * ⚠️ `state` is `string`, not the narrowed union. The CHECK allows four values today and a migration
 * may add a fifth; an unknown value is shown as ITSELF rather than bucketed into `open`, which would
 * report a dropped topic as live.
 */
export type SpaceItemState = 'open' | 'scheduled' | 'done' | 'dropped';
export const SPACE_ITEM_STATES: readonly SpaceItemState[] = ['open', 'scheduled', 'done', 'dropped'];

export interface SpaceItem {
  id: Id;
  space_id: Id;
  title: string;
  body: string | null;
  state: SpaceItemState | string;
  owner: string | null;
  /** ⚠️ `YYYY-MM-DD`, cast `::text` server-side. ⛔ Never `new Date()` arithmetic — a Postgres DATE
   *  read through node-postgres lands at LOCAL midnight and serialises to the PREVIOUS day here. */
  due_on: string | null;
  /** 🔴 THE ONE BRIDGE to dev work. Non-null means this topic became a ticket. */
  promoted_ticket_id: Id | null;
  /**
   * ⭐ WHEN it was promoted, paired with the id by a CHECK (`space_item_promotion_both_or_neither`,
   * db/012) rather than by anyone's care. The API's own reason for keeping it: without it, a topic
   * promoted eighteen months ago and one promoted this morning are the same row, and "how long does a
   * research topic sit before it becomes work" is unanswerable.
   */
  promoted_at: string | null;
  /** ⭐ `TBS-41`, not `41` — assembled server-side from `project.key || '-' || ticket.number`.
   *  NULL when not promoted, which is the same fact as `promoted_ticket_id` being null. */
  promoted_ticket_key: string | null;
  created_at: string;
}

export function isKnownState(state: string): state is SpaceItemState {
  return (SPACE_ITEM_STATES as readonly string[]).includes(state);
}

export const SPACES_PATH = '/spaces';

export async function fetchSpaces(signal?: AbortSignal): Promise<Space[]> {
  const body = await request<unknown>(SPACES_PATH, { signal });
  return asArray<Space>(body, SPACES_PATH);
}

export function spaceItemsPath(spaceId: Id): string {
  return `/spaces/${spaceId}/items`;
}

export async function fetchSpaceItems(spaceId: Id, signal?: AbortSignal): Promise<SpaceItem[]> {
  const path = spaceItemsPath(spaceId);
  const body = await request<unknown>(path, { signal });
  return asArray<SpaceItem>(body, path);
}

/**
 * ⚠️ CAMELCASE ON THE WIRE. The API's zod schema is `{title, body, state, owner, dueOn}` — and zod
 * STRIPS unknown keys by default, so sending `due_on` would return 200 with the date silently
 * dropped. That is the quiet kind of wrong: the item appears, and only its date is missing.
 */
export async function createSpaceItem(
  spaceId: Id,
  body: { title: string; body?: string | null; owner?: string | null; dueOn?: string | null },
): Promise<void> {
  await request<unknown>(spaceItemsPath(spaceId), { method: 'POST', body });
}

/** ⚠️ `/space-items/{id}` — a TOP-LEVEL path, not nested under the space. */
export async function updateSpaceItem(
  itemId: Id,
  body: { title?: string; body?: string | null; state?: string; owner?: string | null; dueOn?: string | null },
): Promise<void> {
  await request<unknown>(`/space-items/${itemId}`, { method: 'PATCH', body });
}

export interface PromoteBody {
  projectId: Id;
  /** ⭐ Defaults to the item's own title. Overridable, because a meeting note is rarely a ticket title. */
  title?: string;
  /** ⭐ OPTIONAL — the API defaults to `task`. Omitting is not the same as guessing. */
  type?: string;
  /** ⭐ OPTIONAL — the API defaults to `medium`. */
  priority?: string;
  /** ⚠️ OMITTED means "the item's owner", which is the API's default and usually right. Sending an
   *  explicit `null` means "nobody", and those are different requests. */
  assignee?: string | null;
  sprintId?: Id | null;
}

/** What `POST /space-items/{id}/promote` returns — 201 `{ item, ticket }`. */
export interface PromoteResult {
  item: SpaceItem;
  ticket: TicketListItem;
}

/**
 * 🔴 PROMOTION — one call, which is what makes it safe.
 *
 * ⭐ The ticket is created and the two promotion columns are written inside ONE transaction, in that
 * order, so a failed creation cannot leave a promotion record pointing at nothing. A client doing
 * create-then-patch can half-succeed into a ticket nothing points at, and neither half is visible
 * afterwards.
 *
 * ⚠️ THREE REFUSALS THIS CLIENT RENDERS RATHER THAN PRE-EMPTS:
 *   · 403 — not an active member of the TARGET project. Without it this endpoint would be a way to
 *     write into a project whose ticket rules would have refused you.
 *   · 409 — already promoted, naming the existing ticket. A second ticket would orphan the first.
 *   · 400 — the sprint belongs to a different project.
 */
export async function promoteSpaceItem(itemId: Id, body: PromoteBody): Promise<PromoteResult> {
  const path = `/space-items/${itemId}/promote`;
  const response = await request<unknown>(path, { method: 'POST', body });
  return {
    item: unwrap<SpaceItem>(response, 'item', path),
    ticket: unwrap<TicketListItem>(response, 'ticket', path),
  };
}
